import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Package, ClipboardList, CheckCircle2, ShieldCheck, Clock, BadgeCheck, Banknote, AlertTriangle } from "lucide-react";
import { PageHeader, StatCard, Card, StatusBadge, Loading } from "../../components/common";
import { api } from "../../lib/api";
import { pkr, fmtDate, ORDER_STATUS } from "../../lib/format";

const Line = ({ k, v, strong, testid }) => (
  <div className={`flex justify-between py-2.5 text-sm ${strong ? "font-semibold" : ""}`}><span className="text-slate-600">{k}</span><span className="font-mono" data-testid={testid}>{v}</span></div>
);

export default function AdminOverview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setD(r.data)); }, []);
  if (!d) return <Loading />;
  return (
    <div className="space-y-8">
      <PageHeader title="Overview" subtitle="Live totals from orders and the commission ledger." />
      <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
        <StatCard testid="ov-executives" label="Registered executives" value={d.executives} icon={Users} />
        <StatCard testid="ov-published-deals" label="Published deals" value={d.published_deals} icon={Package} />
        <StatCard testid="ov-orders" label="Orders received" value={d.orders_received} icon={ClipboardList} hint={`${d.orders_in_progress} in progress`} />
        <StatCard testid="ov-completed" label="Completed orders" value={d.completed_orders} icon={CheckCircle2} tone="green" />
        <StatCard testid="ov-verified-sales" label="Verified sales value" value={pkr(d.verified_sales_value)} icon={ShieldCheck} hint={`${d.verified_orders} payment-verified orders`} />
        <StatCard testid="ov-pending-review" label="Commissions pending review" value={pkr(d.commission_pending_review)} icon={Clock} tone="amber" hint={`${d.commission_pending_review_count} to review`} />
        <StatCard testid="ov-payable" label="Approved, awaiting payment" value={pkr(d.commission_payable)} icon={BadgeCheck} tone="green" hint={`${d.commission_payable_count} commissions`} />
        <StatCard testid="ov-paid" label="Total commissions paid" value={pkr(d.commission_paid)} icon={Banknote} />
      </div>
      {d.recovery_due > 0 && (
        <div className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"><AlertTriangle className="h-5 w-5" />{pkr(d.recovery_due)} in paid commissions were reversed after refunds/cancellations ({d.recovery_due_count}). Recover these from the executives.</div>
      )}
      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="p-5 lg:col-span-2">
          <h2 className="font-heading text-lg font-semibold">Financial position</h2>
          <p className="mb-2 text-xs text-slate-500">Order value is not profit. Commission expense is shown separately.</p>
          <div className="divide-y">
            <Line k="Open order value (excl. cancelled/refunded)" v={pkr(d.open_order_value)} />
            <Line k="Verified sales value (business revenue)" v={pkr(d.verified_sales_value)} strong testid="fin-verified" />
            <Line k="Completed sales value" v={pkr(d.completed_sales_value)} />
            <Line k="Commission expense (approved + paid)" v={`− ${pkr(d.commission_expense)}`} />
            <Line k="Revenue after commissions*" v={pkr(d.revenue_after_commission)} strong testid="fin-net" />
            <Line k="Outstanding commission liability" v={pkr(d.commission_payable)} strong testid="fin-liability" />
            <Line k="Commission awaiting completion / return period" v={pkr(d.commission_pending_completion)} />
          </div>
          <p className="mt-2 text-[11px] text-slate-400">*Before product cost, delivery and other expenses.</p>
        </Card>
        <Card className="p-5 lg:col-span-3">
          <div className="mb-3 flex justify-between"><h2 className="font-heading text-lg font-semibold">Latest orders</h2><Link to="/admin/orders" className="text-sm text-emerald-700 hover:underline">All orders</Link></div>
          <ul className="divide-y">
            {d.recent_orders.length === 0 && <p className="py-6 text-center text-sm text-slate-500">No orders yet</p>}
            {d.recent_orders.map((o) => (
              <li key={o.id}><Link to={`/admin/orders/${o.id}`} className="flex items-center justify-between gap-3 py-3 hover:bg-slate-50">
                <div className="min-w-0"><p className="font-mono text-sm font-bold">{o.order_no} <span className="font-normal text-slate-400">· {o.sales_id}</span></p><p className="truncate text-sm text-slate-500">{o.deal.name} · {fmtDate(o.created_at)}</p></div>
                <div className="text-right"><p className="font-mono text-sm font-bold">{pkr(o.total_amount)}</p><StatusBadge map={ORDER_STATUS} value={o.status} /></div>
              </Link></li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
